import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '../../../services/common/auth.service';
import { LawpayPaymentService } from '../../../services/lawpay/lawpay-payment.service';
import { ContactSearchService } from '../../../services/contacts/contact-search.service';
import { MatterSearchService } from '../../../services/matters/matter-search.service';

@Component({
  selector: 'app-lawpay-payment-queue',
  templateUrl: './lawpay-payment-queue.component.html',
  styleUrls: ['./lawpay-payment-queue.component.css']
})
export class LawpayPaymentQueueComponent implements OnInit {
  payments: any[] = [];
  totalCount: number = 0;
  currentPage: number = 1;
  totalPages: number = 1;
  perPage: number = 25;

  searchParams: any = {
    status: 'unprocessed',
    per_page: 25,
    page: 1
  };

  searchOptions: any = {};
  selectedPayment: any = null;

  // For manual contact/matter matching
  contactSearchTerm: string = '';
  contactSearchResults: any[] = [];
  matterSearchTerm: string = '';
  matterSearchResults: any[] = [];

  selectedContact: any = null;
  selectedMatter: any = null;

  loading: boolean = false;
  processing: boolean = false;
  showContactSearch: boolean = false;
  showMatterSearch: boolean = false;

  constructor(
    private authService: AuthService,
    private lawpayPaymentService: LawpayPaymentService,
    private contactSearchService: ContactSearchService,
    private matterSearchService: MatterSearchService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadSearchOptions();
    this.search();
  }

  loadSearchOptions(): void {
    this.lawpayPaymentService.getSearchOptions().then(options => {
      this.searchOptions = options;
    });
  }

  search(): void {
    this.loading = true;
    this.lawpayPaymentService.index(this.searchParams).then(result => {
      this.payments = result.payments;
      this.totalCount = result.total_count;
      this.currentPage = result.current_page;
      this.totalPages = result.total_pages;
      this.perPage = result.per_page;
      this.loading = false;
    }).catch(error => {
      console.error('Error loading payments:', error);
      this.loading = false;
    });
  }

  pageChanged(event: any): void {
    this.searchParams.page = event.page;
    this.search();
  }

  selectPayment(payment: any): void {
    this.selectedPayment = payment;
    this.selectedContact = null;
    this.selectedMatter = null;
    this.showContactSearch = false;
    this.showMatterSearch = false;

    // Load full payment details with auto-matched contact/matter
    this.lawpayPaymentService.show(payment.id).then(detail => {
      this.selectedPayment = detail;
      if (detail.contact_id) {
        this.selectedContact = { id: detail.contact_id, name: detail.contact_name };
      }
      if (detail.matter_id) {
        this.selectedMatter = { id: detail.matter_id, number: detail.matter_number, name: detail.matter_name };
      }
    });
  }

  // Contact search
  toggleContactSearch(): void {
    this.showContactSearch = !this.showContactSearch;
    if (this.showContactSearch) {
      this.contactSearchTerm = '';
      this.contactSearchResults = [];
    }
  }

  searchContacts(): void {
    if (!this.contactSearchTerm || this.contactSearchTerm.length < 2) {
      this.contactSearchResults = [];
      return;
    }
    this.contactSearchService.contactsFieldSearch(this.contactSearchTerm).subscribe(results => {
      this.contactSearchResults = results;
    });
  }

  selectContact(contact: any): void {
    this.selectedContact = contact;
    this.contactSearchResults = [];
    this.contactSearchTerm = '';
    this.showContactSearch = false;
    // Clear matter when contact changes
    this.selectedMatter = null;
  }

  clearContact(): void {
    this.selectedContact = null;
    this.selectedMatter = null;
  }

  // Matter search
  toggleMatterSearch(): void {
    this.showMatterSearch = !this.showMatterSearch;
    if (this.showMatterSearch) {
      this.matterSearchTerm = '';
      this.matterSearchResults = [];
    }
  }

  searchMatters(): void {
    if (!this.matterSearchTerm || this.matterSearchTerm.length < 2) {
      this.matterSearchResults = [];
      return;
    }
    this.matterSearchService.mattersFieldSearch(this.matterSearchTerm).subscribe(results => {
      this.matterSearchResults = results;
    });
  }

  selectMatter(matter: any): void {
    this.selectedMatter = matter;
    this.matterSearchResults = [];
    this.matterSearchTerm = '';
    this.showMatterSearch = false;
  }

  clearMatter(): void {
    this.selectedMatter = null;
  }

  // Process to receipt
  processToReceipt(): void {
    if (!this.selectedPayment) return;
    if (!this.canManualProcess()) return;

    this.processing = true;
    this.lawpayPaymentService.processPayment(
      this.selectedPayment.id,
      this.selectedContact?.id,
      this.selectedMatter?.id
    ).then(result => {
      this.processing = false;
      window.location.href = result.redirect_url || '/lawpay_payments_processed';
    }).catch(error => {
      this.processing = false;
      console.error('Error processing payment:', error);
      alert(this.processErrorMessage(error));
    });
  }

  canManualProcess(): boolean {
    return !!this.selectedPayment &&
      this.selectedPayment.processing_status !== 'processed' &&
      !!this.selectedMatter;
  }

  viewEntryGroup(payment: any): void {
    if (payment.entry_group_url) {
      window.location.href = payment.entry_group_url;
    } else if (payment.entry_group_id) {
      window.location.href = `/entry_groups/${payment.entry_group_id}`;
    }
  }

  processErrorMessage(error: any): string {
    return error?.error?.error ||
      error?.error?.message ||
      error?.message ||
      'Error processing LawPay payment. Please try again.';
  }

  buildQueryString(params: any): string {
    return Object.keys(params)
      .filter(key => params[key] !== null && params[key] !== undefined)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');
  }

  cancelSelection(): void {
    this.selectedPayment = null;
    this.selectedContact = null;
    this.selectedMatter = null;
    this.showContactSearch = false;
    this.showMatterSearch = false;
  }

  // Refresh the list
  refresh(): void {
    this.search();
  }

  // Format date for display
  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}
